/**
 * Punchlist detail screen.
 *
 * Shows a punchlist header (title / status / gating) and its items (GET items). Each
 * item shows its status and contextual actions:
 *   - suggested items (source 'suggested') → Accept / Reject (respondItem)
 *   - internal items in ready_for_review → Verify (verifyItem)
 *   - any open/in-progress item → advance status (updateItem PATCH)
 * An inline "Add item" form appends an item. "Sign off" and "Regenerate suggestions"
 * act on the whole punchlist. All mutations invalidate the relevant queries.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPunchlist,
  listPunchlistItems,
  addPunchlistItem,
  signOffPunchlist,
  regenerate,
  updateItem,
  verifyItem,
  respondItem,
  type PunchlistItemRow,
} from '../../../src/api/punchlists';
import { Screen, Text, Badge, Card, Button, Field, Select } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize, formatDueRelative } from '../../../src/lib/format';
import type { StatusKey } from '../../../src/theme';

// Punchlist status → badge.
function punchlistBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'signed_off':
      return { key: 'paid', label: 'Signed off' };
    case 'complete':
      return { key: 'paid', label: 'Complete' };
    case 'in_progress':
      return { key: 'inProgress', label: 'In progress' };
    case 'void':
      return { key: 'draft', label: 'Void' };
    default:
      return { key: 'sent', label: 'Open' };
  }
}

// Item status → badge. open, in_progress, ready_for_review, verified, wont_do, cancelled.
function itemBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'verified':
      return { key: 'paid', label: 'Verified' };
    case 'ready_for_review':
      return { key: 'sent', label: 'Ready for review' };
    case 'in_progress':
      return { key: 'inProgress', label: 'In progress' };
    case 'wont_do':
      return { key: 'draft', label: "Won't do" };
    case 'cancelled':
      return { key: 'draft', label: 'Cancelled' };
    default:
      return { key: 'overdue', label: 'Open' };
  }
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
] as const;

export default function PunchlistDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const punchlistId = String(id);
  const queryClient = useQueryClient();

  const [description, setDescription] = useState('');
  const [areaLocation, setAreaLocation] = useState('');
  const [priority, setPriority] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const punchlistQuery = useQuery({
    queryKey: ['punchlist', punchlistId],
    queryFn: () => getPunchlist(punchlistId),
    staleTime: 30_000,
  });

  const itemsQuery = useQuery({
    queryKey: ['punchlist', punchlistId, 'items'],
    queryFn: () => listPunchlistItems(punchlistId),
    staleTime: 30_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['punchlist', punchlistId] });
    void queryClient.invalidateQueries({ queryKey: ['punchlist', punchlistId, 'items'] });
    void queryClient.invalidateQueries({ queryKey: ['punchlists'] });
  };

  const addMutation = useMutation({
    mutationFn: (body: { description: string; area_location?: string; priority?: string }) =>
      addPunchlistItem(punchlistId, body),
    onSuccess: () => {
      setDescription('');
      setAreaLocation('');
      setPriority(null);
      setFormError(null);
      invalidate();
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not add item.')),
  });

  const signOffMutation = useMutation({
    mutationFn: () => signOffPunchlist(punchlistId),
    onSuccess: invalidate,
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not sign off.')),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => regenerate(punchlistId),
    onSuccess: invalidate,
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not regenerate suggestions.')),
  });

  const updateItemMutation = useMutation({
    mutationFn: (vars: { itemId: string; status: string }) =>
      updateItem(vars.itemId, { status: vars.status }),
    onSuccess: invalidate,
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not update item.')),
  });

  const verifyMutation = useMutation({
    mutationFn: (itemId: string) => verifyItem(itemId),
    onSuccess: invalidate,
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not verify item.')),
  });

  const respondMutation = useMutation({
    mutationFn: (vars: { itemId: string; action: 'accept' | 'reject' }) =>
      respondItem(vars.itemId, vars.action),
    onSuccess: invalidate,
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not respond to item.')),
  });

  const onAdd = () => {
    setFormError(null);
    if (!description.trim()) {
      setFormError('Enter a description for the item.');
      return;
    }
    const body: { description: string; area_location?: string; priority?: string } = {
      description: description.trim(),
    };
    if (areaLocation.trim()) body.area_location = areaLocation.trim();
    if (priority) body.priority = priority;
    addMutation.mutate(body);
  };

  if (punchlistQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading punchlist"
          />
          <Text variant="body" color="secondary">
            Loading punchlist…
          </Text>
        </View>
      </Screen>
    );
  }

  if (punchlistQuery.isError || !punchlistQuery.data) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(punchlistQuery.error, 'Failed to load punchlist.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => punchlistQuery.refetch()}
              accessibilityLabel="Retry loading punchlist"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const punchlist = punchlistQuery.data;
  const b = punchlistBadge(punchlist.status);
  const items = itemsQuery.data ?? [];
  const isSignedOff = punchlist.status === 'signed_off' || punchlist.status === 'void';
  const actionsBusy =
    signOffMutation.isPending ||
    regenerateMutation.isPending ||
    updateItemMutation.isPending ||
    verifyMutation.isPending ||
    respondMutation.isPending;

  return (
    <Screen scroll>
      {/* Header */}
      <View style={styles.header}>
        <Text variant="title">{punchlist.title}</Text>
        <View style={styles.headerMeta}>
          <Badge status={b.key} label={b.label} />
          {punchlist.is_gating ? <Badge status="inProgress" label="Gating" /> : null}
        </View>
      </View>

      {actionError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {actionError}
          </Text>
        </Card>
      ) : null}

      {/* Whole-punchlist actions */}
      <View style={styles.actions}>
        <Button
          title="Regenerate suggestions"
          variant="secondary"
          onPress={() => {
            setActionError(null);
            regenerateMutation.mutate();
          }}
          loading={regenerateMutation.isPending}
          disabled={actionsBusy || isSignedOff}
          accessibilityLabel="Regenerate suggested items"
        />
        <Button
          title="Sign off"
          variant="accent"
          onPress={() => {
            setActionError(null);
            signOffMutation.mutate();
          }}
          loading={signOffMutation.isPending}
          disabled={actionsBusy || isSignedOff}
          accessibilityLabel="Sign off punchlist"
        />
      </View>

      {/* Items */}
      <Text variant="subhead">Items</Text>
      {itemsQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading items" />
      ) : itemsQuery.isError ? (
        <Card>
          <Text variant="body" color="danger">
            {extractErrorMessage(itemsQuery.error, 'Failed to load items.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => itemsQuery.refetch()}
          />
        </Card>
      ) : items.length === 0 ? (
        <Text variant="body" color="secondary">
          No items yet. Add one below or regenerate suggestions.
        </Text>
      ) : (
        <View style={styles.list}>
          {items.map((item: PunchlistItemRow) => {
            const ib = itemBadge(item.status);
            const isSuggested = item.source === 'suggested' && item.status === 'open';
            const due = formatDueRelative(item.due_at);
            return (
              <Card key={item.id}>
                <Text variant="bodyStrong">{item.description}</Text>
                <View style={styles.itemMeta}>
                  <Badge status={ib.key} label={ib.label} />
                  {item.priority ? (
                    <Text variant="small" color="secondary">
                      {humanize(item.priority)}
                    </Text>
                  ) : null}
                  {item.area_location ? (
                    <Text variant="small" color="secondary">
                      {item.area_location}
                    </Text>
                  ) : null}
                  {item.due_at ? (
                    <Text variant="small" color={due.overdue ? 'danger' : 'secondary'}>
                      {due.text}
                    </Text>
                  ) : null}
                </View>

                {!isSignedOff ? (
                  <View style={styles.itemActions}>
                    {isSuggested ? (
                      <>
                        <Button
                          title="Accept"
                          size="sm"
                          onPress={() => {
                            setActionError(null);
                            respondMutation.mutate({ itemId: item.id, action: 'accept' });
                          }}
                          loading={
                            respondMutation.isPending &&
                            respondMutation.variables?.itemId === item.id
                          }
                          disabled={actionsBusy}
                          fullWidth={false}
                          accessibilityLabel="Accept suggested item"
                        />
                        <Button
                          title="Reject"
                          variant="danger"
                          size="sm"
                          onPress={() => {
                            setActionError(null);
                            respondMutation.mutate({ itemId: item.id, action: 'reject' });
                          }}
                          loading={
                            respondMutation.isPending &&
                            respondMutation.variables?.itemId === item.id
                          }
                          disabled={actionsBusy}
                          fullWidth={false}
                          accessibilityLabel="Reject suggested item"
                        />
                      </>
                    ) : (
                      <>
                        {item.status === 'open' ? (
                          <Button
                            title="Start"
                            variant="secondary"
                            size="sm"
                            onPress={() => {
                              setActionError(null);
                              updateItemMutation.mutate({ itemId: item.id, status: 'in_progress' });
                            }}
                            loading={
                              updateItemMutation.isPending &&
                              updateItemMutation.variables?.itemId === item.id
                            }
                            disabled={actionsBusy}
                            fullWidth={false}
                            accessibilityLabel="Mark item in progress"
                          />
                        ) : null}
                        {item.status === 'in_progress' ? (
                          <Button
                            title="Ready for review"
                            variant="secondary"
                            size="sm"
                            onPress={() => {
                              setActionError(null);
                              updateItemMutation.mutate({
                                itemId: item.id,
                                status: 'ready_for_review',
                              });
                            }}
                            loading={
                              updateItemMutation.isPending &&
                              updateItemMutation.variables?.itemId === item.id
                            }
                            disabled={actionsBusy}
                            fullWidth={false}
                            accessibilityLabel="Mark item ready for review"
                          />
                        ) : null}
                        {item.status === 'ready_for_review' ? (
                          <Button
                            title="Verify"
                            size="sm"
                            onPress={() => {
                              setActionError(null);
                              verifyMutation.mutate(item.id);
                            }}
                            loading={
                              verifyMutation.isPending &&
                              verifyMutation.variables === item.id
                            }
                            disabled={actionsBusy}
                            fullWidth={false}
                            accessibilityLabel="Verify item"
                          />
                        ) : null}
                      </>
                    )}
                  </View>
                ) : null}
              </Card>
            );
          })}
        </View>
      )}

      {/* Add item form */}
      {!isSignedOff ? (
        <Card>
          <Text variant="bodyStrong">Add item</Text>
          <Field
            label="Description"
            placeholder="e.g. Touch up paint in hallway"
            value={description}
            onChangeText={setDescription}
            autoCapitalize="sentences"
            accessibilityLabel="Item description"
          />
          <Field
            label="Area / location (optional)"
            placeholder="e.g. Master bath"
            value={areaLocation}
            onChangeText={setAreaLocation}
            autoCapitalize="sentences"
            accessibilityLabel="Item area or location"
          />
          <Select
            label="Priority (optional)"
            options={PRIORITY_OPTIONS}
            value={priority}
            onChange={setPriority}
          />
          {formError ? (
            <Text variant="small" color="danger" accessibilityRole="alert">
              {formError}
            </Text>
          ) : null}
          <Button
            title="Add item"
            onPress={onAdd}
            loading={addMutation.isPending}
            disabled={addMutation.isPending || !description.trim()}
            accessibilityLabel="Add item to punchlist"
          />
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  header: { gap: theme.spacing.sm },
  headerMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
  actions: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
  itemMeta: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing.sm },
  itemActions: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
});
